import React, { useCallback, useEffect, useState } from 'react'

import { InputPartial } from '@driftt/dds'

import useCustomMeetingTypes from 'hooks/useCustomMeetingTypes'
import useOrgMeetingTypes from 'hooks/useOrgMeetingTypes'
import { MeetingTypeDict } from 'types/MeetingType'
import { slugify } from 'utils/index'

interface MeetingLinkInputProps {
  meetingId: string
  meetingName: string
  profilesPath: string
  readOnly: boolean
  error: string
  setLinkError: (newError: string) => void
  getSlug: () => string
  updateState: (slug: string) => void
}

const MeetingLinkInput = ({
  meetingId,
  meetingName,
  profilesPath,
  readOnly,
  error,
  setLinkError,
  getSlug,
  updateState,
}: MeetingLinkInputProps) => {
  const currentMeetingLink = getSlug()
  const [meetingSlugs, setMeetingSlugs] = useState<Array<any>>([])

  const {
    isLoading: customIsLoading,
    isError: customIsError,
    meetingTypes: customMeetingTypes,
  } = useCustomMeetingTypes()
  const {
    isLoading: orgIsLoading,
    isError: orgIsError,
    meetingTypes: orgMeetingTypes,
  } = useOrgMeetingTypes()
  const isLoading = customIsLoading || orgIsLoading
  const isError = customIsError || orgIsError

  const getSlugsFromMeetingTypes = useCallback(
    (types: MeetingTypeDict) => {
      // dict format
      // {[<meetingId>]: {...meeting type content...}}
      return Object.entries(types)
        .filter(
          //meetings other than this one
          (keyValue) => keyValue[0] !== meetingId
        )
        .map((keyValue) =>
          //if the meeting doesn't have a custom link saved, the slug is the meeting id
          keyValue[1].conferenceLink ? keyValue[1].conferenceLink : keyValue[0]
        )
    },
    [meetingId]
  )

  useEffect(() => {
    if (isLoading || isError) {
      setMeetingSlugs([])
    } else if (customMeetingTypes && orgMeetingTypes) {
      const meetingsSlugs = getSlugsFromMeetingTypes(customMeetingTypes)
      const orgMeetingSlugs = getSlugsFromMeetingTypes(orgMeetingTypes)
      setMeetingSlugs([...meetingsSlugs, ...orgMeetingSlugs])
    }
  }, [
    customMeetingTypes,
    getSlugsFromMeetingTypes,
    isError,
    isLoading,
    meetingId,
    orgMeetingTypes,
  ])

  const validateSlug = (value: string) => {
    if (!/^[a-zA-Z0-9_-]*$/g.test(value)) {
      setLinkError(
        'The link can only contain letters, numbers, dashes, and underscores'
      )
    }
    if (meetingSlugs.includes(value)) {
      setLinkError('This link is already used by another meeting type')
    } else {
      setLinkError('')
    }
  }

  //conference link reset to standard slug based on name if user clears the input
  const [blankError, setBlankError] = useState('')
  const onLinkChange = (value: string) => {
    if (value === '') {
      updateState(slugify(meetingName))
      setLinkError('')
      setBlankError('The meeting link cannot be blank')
      setTimeout(() => {
        setBlankError('')
      }, 2000)
    } else {
      validateSlug(value)
      updateState(value)
    }
  }

  return (
    <InputPartial
      size={'x-large'}
      label="Meeting link URL"
      prefix={`${profilesPath}/`}
      value={currentMeetingLink}
      onChange={(e) => {
        onLinkChange(e.target.value)
      }}
      enforceHardWidth={true}
      disabled={readOnly}
      errorMessage={error !== '' ? error : blankError}
    />
  )
}

export default MeetingLinkInput
